import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from './Layout';
import Dashboard from './Dashboard';
import SystemStatus from './admin/SystemStatus';
import UserManagement from './admin/UserManagement';
import ClaimsManagement from './admin/ClaimsManagement';
import Controls from './admin/Controls';
import TokenPrice from './admin/TokenPrice';
import Allocations from './admin/Allocations';
import LevelIncome from './admin/LevelIncome';
import Currencies from './admin/Currencies';
import SmartContract from './admin/SmartContract';
import AdminDistribution from './admin/AdminDistribution';
import Wallets from './admin/Wallets';
import AdminLedger from './admin/AdminLedger';
import RecentActivity from './admin/RecentActivity';
import Settings from './admin/Settings';
import WalletSpend from './admin/WalletSpend';
import SellWithdrawals from './admin/SellWithdrawals';
import RoyaltyAdmin from './admin/RoyaltyAdmin';
import AppearanceAdmin from './admin/AppearanceAdmin';
import WebsiteMode from './admin/WebsiteMode';
import TestConsole from './admin/TestConsole';
import { applyAccent } from './Appearance';


import { getAdminConfig, getPrice, getPendingCounts, adminImpersonateUser, setUserToken, clearUserToken, getUserToken } from '../utils/api';
import './AdminPanel.css';

const NAV = [
  {
    group: 'overview', label: 'Overview', items: [
      { key: 'status', label: 'Dashboard', icon: '◈' },
      { key: 'activity', label: 'All Transactions', icon: '⊕' },
      { key: 'ledger', label: 'Ledger', icon: '☰' },
    ],
  },
  {
    group: 'members', label: 'Members & Requests', items: [
      { key: 'users', label: 'Users', icon: '☷' },
      { key: 'claims', label: 'Level Income Claims', icon: '✎' },
      { key: 'sell-withdrawals', label: 'Sell Withdrawals', icon: '⇩' },
      { key: 'royalty', label: 'Royalty', icon: '♛' },
    ],
  },
  {
    group: 'funds', label: 'Wallets & Funds', items: [
      { key: 'wallets', label: 'System Wallets', icon: '⊡' },
      { key: 'spend', label: 'Wallet Spend', icon: '⇄' },
    ],
  },
  {
    group: 'rules', label: 'Rules & Economics', items: [
      { key: 'controls', label: 'Buy & Sell Controls', icon: '⚙' },
      { key: 'price', label: 'Token Price', icon: '$' },
      { key: 'allocation', label: 'Purchase Allocation', icon: '%' },
      { key: 'levels', label: 'Level Income', icon: '↗' },
      { key: 'admin-dist', label: 'Admin Distribution', icon: '÷' },
      { key: 'currencies', label: 'Currencies', icon: '₿' },
    ],
  },
  {
    group: 'system', label: 'System', items: [
      { key: 'website', label: 'Website Mode', icon: '◐' },
      { key: 'appearance', label: 'Appearance & Notify', icon: '✦' },
      { key: 'test', label: 'Test Console', icon: '⚗' },
      { key: 'contract', label: 'Token Contract', icon: '◇' },
      { key: 'settings', label: 'Settings', icon: '⚑' },
    ],
  },
];

const AdminPanel = ({ onBack, adminInfo }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState('status');
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [cryptoPrices, setCryptoPrices] = useState({});
  const [pricesLoaded, setPricesLoaded] = useState(false);
  const [showUsdt, setShowUsdt] = useState(false);
  const [pending, setPending] = useState({});

  // ─── Read-only "view as user" (impersonation) ───
  const [impersonation, setImpersonation] = useState(null);
  const priorUserTokenRef = useRef(null);

  const flash = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 3500);
  };

  const startImpersonation = useCallback(async (roboId) => {
    try {
      const res = await adminImpersonateUser(roboId);
      if (!res?.token || !res?.wallet) { flash('Could not open read-only view', 'error'); return; }
      // Preserve any existing user session token, then swap in the read-only one
      // so the reused Dashboard's read calls authenticate as this user.
      priorUserTokenRef.current = getUserToken();
      setUserToken(res.token);
      setImpersonation({ wallet: res.wallet, roboId: res.roboId, referralCode: res.referralCode });
    } catch {
      flash('Could not open read-only view', 'error');
    }
  }, []);

  const stopImpersonation = useCallback(() => {
    clearUserToken();
    if (priorUserTokenRef.current) setUserToken(priorUserTokenRef.current);
    priorUserTokenRef.current = null;
    setImpersonation(null);
  }, []);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try { const res = await getAdminConfig(); setConfig(res.config); if (res.config?.themeAccent) applyAccent(res.config.themeAccent); } catch { flash('Failed to load', 'error'); }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadConfig();
    // Fetch crypto prices once for all admin pages. `pricesLoaded` gates the
    // dashboard render so values are never shown before the BNB rate is known
    // (otherwise the USDT→BNB conversion falls back to ÷1 and displays raw USDT
    // numbers labelled as BNB until the rate arrives — the "wrong until refresh" bug).
    getPrice()
      .then(r => { if (r.cryptoPrices) setCryptoPrices(r.cryptoPrices); })
      .catch(() => {})
      .finally(() => setPricesLoaded(true));
  }, [loadConfig]);

  // Poll pending-action counts for the nav badges (refresh on page switch too).
  useEffect(() => {
    let alive = true;
    const loadPending = () => getPendingCounts().then(r => { if (alive && r.counts) setPending(r.counts); }).catch(() => {});
    loadPending();
    const t = setInterval(loadPending, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [page]);

  const navWithBadges = NAV.map(g => ({
    ...g,
    items: (g.items || []).map(it => ({ ...it, badge: pending[it.key] || 0 })),
  }));

  // Conversion helpers — used by all admin sub-components
  const f = (n, d = 4) => (n == null || isNaN(n)) ? '0' : parseFloat(n).toFixed(d);
  const BC = config?.baseCurrency || 'USDT';
  const SYM = config?.tokenSymbol || 'ROBO';
  const bcRate = cryptoPrices[BC] || 1;
  const isNotUsdt = BC !== 'USDT';

  const toBC = (usdtVal) => {
    if (!usdtVal) return 0;
    if (BC === 'USDT') return usdtVal;
    return bcRate > 0 ? usdtVal / bcRate : 0;
  };
  const fBC = (usdtVal, dec = 4) => f(toBC(usdtVal), dec);

  // Show value with optional USDT equivalent
  const vBC = (usdtVal, dec = 4) => {
    const bcVal = fBC(usdtVal, dec);
    if (!isNotUsdt || !showUsdt) return `${bcVal} ${BC}`;
    return `${bcVal} ${BC} ≈ ${f(usdtVal, 2)} USDT`;
  };

  const sharedProps = {
    config, loadConfig, flash, adminInfo,
    f, BC, SYM, bcRate, toBC, fBC, vBC,
    isNotUsdt, showUsdt, setShowUsdt, cryptoPrices,
    onImpersonate: startImpersonation,
  };

  // Read-only "view as user": render the REAL user dashboard (same components),
  // scoped to this user's wallet, with all money actions disabled.
  if (impersonation) {
    return (
      <Dashboard
        viewAsAccount={impersonation.wallet}
        readOnly
        impersonationLabel={impersonation.referralCode || impersonation.roboId}
        onExitImpersonation={stopImpersonation}
        onLogout={stopImpersonation}
      />
    );
  }

  // When base currency is non-USDT (e.g. BNB), wait for the conversion rate before
  // rendering — but never hang: once the price fetch has finished (pricesLoaded),
  // render anyway even if it returned no rate. Prevents the unconverted-USDT flash.
  const rateReady = !isNotUsdt || cryptoPrices[BC] > 0 || pricesLoaded;

  if (loading || !config || !rateReady) {
    return <Layout nav={navWithBadges} activeNav={page} onNavChange={setPage} title="ADMIN" headerLabel={adminInfo?.name || adminInfo?.username || 'Admin'}>
      <div className="page-loading"><div className="spinner" /><p className="text-3">Loading...</p></div>
    </Layout>;
  }

  return (
    <Layout nav={navWithBadges} activeNav={page} onNavChange={setPage} onLogout={onBack} title="ADMIN" headerLabel={adminInfo?.name || adminInfo?.username || 'Admin'}>
      {msg.text && <div className={`adm-toast notice notice-${msg.type}`}>{msg.text}</div>}

      {/* USDT Toggle — shown on all pages when base currency is not USDT */}
      {isNotUsdt && (
        <div className="d-usdt-toggle">
          <label className="d-toggle-label">
            <input type="checkbox" checked={showUsdt} onChange={() => setShowUsdt(!showUsdt)} />
            <span>Show USDT equivalent</span>
          </label>
          {showUsdt && <span className="d-usdt-rate">1 {BC} = {f(bcRate, 2)} USDT</span>}
        </div>
      )}

      {page === 'status' && <SystemStatus {...sharedProps} />}
      {page === 'activity' && <RecentActivity {...sharedProps} />}
      {page === 'ledger' && <AdminLedger {...sharedProps} />}
      {page === 'users' && <UserManagement {...sharedProps} />}
      {page === 'claims' && <ClaimsManagement {...sharedProps} />}
      {page === 'controls' && <Controls {...sharedProps} />}
      {page === 'website' && <WebsiteMode {...sharedProps} />}
      {page === 'appearance' && <AppearanceAdmin {...sharedProps} />}
      {page === 'test' && <TestConsole {...sharedProps} />}
      {page === 'wallets' && <Wallets {...sharedProps} />}
      {page === 'sell-withdrawals' && <SellWithdrawals {...sharedProps} />}
      {page === 'royalty' && <RoyaltyAdmin {...sharedProps} />}
      {page === 'spend' && <WalletSpend {...sharedProps} />}
      {page === 'price' && <TokenPrice {...sharedProps} />}
      {page === 'allocation' && <Allocations {...sharedProps} />}
      {page === 'levels' && <LevelIncome {...sharedProps} />}
      {page === 'currencies' && <Currencies {...sharedProps} />}
      {page === 'contract' && <SmartContract {...sharedProps} />}
      {page === 'admin-dist' && <AdminDistribution {...sharedProps} />}
      {page === 'settings' && <Settings {...sharedProps} onLogout={onBack} />}
    </Layout>
  );
};

export default AdminPanel;